import {Component, computed, inject, input, OnInit, output, signal} from '@angular/core';
import {FormActionButtonsComponent} from "../../common/forms/form-action-buttons/form-action-btns.component";
import {FormButtonComponent} from "../../common/forms/form-button/form-button.component";
import {AppAlertService, ParametersService, SubtitlesService, ZonesSettingsService} from "../../core";
import {ActionType, AlertDialog, ChangeZoneNameFormValue, ZoneIconNameAndId, ZonesListItem} from "../../models";
import {FormLabelComponent} from "../../common/forms/form-label/form-label.component";
import {FormTextInputComponent} from "../../common/forms/form-text-input/form-text-input.component";
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ZoneIconComponent} from "../../icons/zone-icon/zone-icon.component";
import {toSignal} from "@angular/core/rxjs-interop";

type ChangeNameForm = FormGroup<{
    newName: FormControl<ChangeZoneNameFormValue['newName']>;
}>;

@Component({
    selector: 'change-settings-dialog',
    standalone: true,
    imports: [
        FormActionButtonsComponent,
        FormButtonComponent,
        FormLabelComponent,
        FormTextInputComponent,
        ReactiveFormsModule,
        ZoneIconComponent
    ],
    templateUrl: './change-settings-dialog.component.html',
    styleUrl: './change-settings-dialog.component.scss'
})
export class ChangeSettingsDialogComponent implements OnInit {
    private readonly appAlert = inject(AppAlertService);
    private readonly fb = inject(FormBuilder);
    private readonly parameters = inject(ParametersService);
    private readonly subtitlesService = inject(SubtitlesService);
    private readonly zonesSettingsService = inject(ZonesSettingsService);

    readonly action = input.required<ActionType>();
    readonly selectedZone = input.required<ZonesListItem>();
    readonly goBack = output();
    readonly closeDialog = output();

    protected readonly labels = {
        turnOnZone: this.subtitlesService.setSubtitle(1163),
        turnOffZone: this.subtitlesService.setSubtitle(917),
        resetZone: this.subtitlesService.setSubtitle(1809),
        cancel: this.subtitlesService.setSubtitle(71),
        zoneName: this.subtitlesService.setSubtitle(3922),
        iconName: this.subtitlesService.setSubtitle(2910),
        change: this.subtitlesService.setSubtitle(1006),
        demoAccountMessage: this.subtitlesService.setSubtitle(3529),
        isUpdating: this.subtitlesService.setSubtitle(1786),
        errorText: this.subtitlesService.setSubtitle(1941),
        defaultZoneName: this.subtitlesService.setSubtitle(1686),
        ok: this.subtitlesService.setSubtitle(475),
    }

    protected readonly maxZoneNameLength = 12;
    protected readonly changeNameForm: ChangeNameForm = this.fb.nonNullable.group({
        newName: ['', [Validators.required, Validators.maxLength(this.maxZoneNameLength)]],
    });
    private readonly newName = toSignal(this.newNameControl.valueChanges, {
        initialValue: this.newNameControl.value,
    });
    protected readonly zoneNameCounter = computed(
        () => `${this.newName().length}/${this.maxZoneNameLength}`
    );
    protected readonly selectedIcon = signal<ZoneIconNameAndId | null>(null);
    protected readonly zoneNamePlaceholder = computed(
        () => `${this.labels.defaultZoneName} ${this.selectedZone().index + 1}`
    );
    protected readonly actionLabel = computed(() => {
        switch (this.action()) {
            case 'turnOff':
                return this.labels.turnOffZone;
            case 'turnOn':
                return this.labels.turnOnZone;
            case 'reset':
                return this.labels.resetZone;
            case 'changeName':
            case 'changeIcon':
                return this.labels.change;
        }
    });
    private readonly actionDialogs = computed<{ updatingDialog: AlertDialog; errorDialog: AlertDialog }>(() => ({
        updatingDialog: {
            title: this.selectedZone().name,
            message: this.labels.isUpdating,
            okButtonText: this.labels.ok,
        },
        errorDialog: {
            title: this.selectedZone().name,
            message: this.labels.errorText,
            okButtonText: this.labels.ok,
        }
    }));

    protected readonly icons: ZoneIconNameAndId[] = [
        {iconName: 'living_room', iconId: 0},
        {iconName: 'bathroom', iconId: 1},
        {iconName: 'kitchen', iconId: 2},
        {iconName: 'children', iconId: 3},
        {iconName: 'bedroom', iconId: 4},
        {iconName: 'wardrobe', iconId: 5},
        {iconName: 'corridor', iconId: 6},
        {iconName: 'garage', iconId: 7}
    ];

    ngOnInit(): void {
        this.changeNameForm.patchValue({
            newName: this.selectedZone().name,
        });
        this.selectedIcon.set(this.icons.find(icon => icon.iconName === this.selectedZone().icon) || null);
    }

    protected get newNameControl() {
        return this.changeNameForm.controls.newName;
    }

    protected closeDialogClick() {
        this.goBack.emit();
    }

    private changeName(): boolean {
        if (this.changeNameForm.invalid) {
            this.changeNameForm.markAllAsTouched();
            return false;
        }
        const dialogs = this.actionDialogs();
        this.zonesSettingsService.changeZoneName(
            this.selectedZone(),
            this.newNameControl.value
        ).subscribe({
            error: () => this.appAlert.openAlertDialog(dialogs.errorDialog),
        });
        return true;
    }

    protected selectIcon(icon: ZoneIconNameAndId): void {
        this.selectedIcon.set(icon);
    }

    private changeIcon(): boolean {
        const selectedIcon = this.selectedIcon();
        if (selectedIcon === null) {
            return false;
        }

        const dialogs = this.actionDialogs();
        this.zonesSettingsService.changeIcon(
            this.selectedZone(),
            selectedIcon.iconId
        ).subscribe({
            error: () => this.appAlert.openAlertDialog(dialogs.errorDialog),
        });
        return true;
    }

    protected executeAction(): void {
        if (this.parameters.demoAccount() ){
            this.appAlert.openAlertDialog({
                title: this.labels.zoneName,
                message: this.labels.demoAccountMessage,
                okButtonText: this.labels.ok,
            })
            return;
        }

        let shouldCloseDialog = false;
        switch (this.action()) {
            case 'changeName':
                shouldCloseDialog = this.changeName();
                break;
            case 'changeIcon':
                shouldCloseDialog = this.changeIcon();
                break;
            case 'turnOff':
                shouldCloseDialog = this.executeZoneAction('turnOff');
                break;
            case 'turnOn':
                shouldCloseDialog = this.executeZoneAction('turnOn');
                break;
            case 'reset':
                shouldCloseDialog = this.executeZoneAction('reset');
                break;
        }
        if (shouldCloseDialog) {
            this.closeDialog.emit();
        }
    }

    private executeZoneAction(action: 'turnOn' | 'turnOff' | 'reset'): boolean {
        const dialogs = this.actionDialogs();
        this.appAlert.openAlertDialog(dialogs.updatingDialog);

        const request = action === 'reset'
            ? this.zonesSettingsService.resetZone(this.selectedZone())
            : this.zonesSettingsService.turnOnOffZone(
                this.selectedZone(),
                action === 'turnOn' ? 'zoneOn' : 'zoneOff'
            );

        request.subscribe({
            error: () => this.appAlert.openAlertDialog(dialogs.errorDialog),
        });
        return true;
    }
}
